import { CSSInterpolation } from '@emotion/css/create-instance'
import React from 'react'

import { ExtendedPaletteColorName } from '../../shared/MobiusColors'
import {
  type ColorToken,
  CssVars,
  NeutralColorToken,
  type ProductTheme,
} from '../themes'
import { Pictogram } from './internal/Pictogram'
import type { Pictogram as PictogramType } from './pictograms'
import {
  getColorDefinition,
  getColorValue,
  useTheme,
  useThemeElevation,
} from './ThemeProvider'
import { Type } from './Type'

const POG_SIZES = {
  hero: 80,
  standard: 48,
}

// The caller must specify exactly one of these two properties.
export type TextOrPictogram =
  | {
      pictogram: PictogramType
      text?: never
    }
  | {
      text: string
      pictogram?: never
    }

type Props = {
  isDisabled?: boolean
  size?: keyof typeof POG_SIZES
  backgroundColorOverride?: ColorToken | null
  foregroundColorOverride?: ColorToken | null
  /**
   * Pass a CSS object if you need to override styles on the root element. Please do this sparingly.
   */
  overrideRootStyles?: CSSInterpolation
} & TextOrPictogram

export function Pog({
  backgroundColorOverride,
  foregroundColorOverride,
  isDisabled = false,
  overrideRootStyles,
  size = 'standard',
  ...restProps
}: Props) {
  const theme = useTheme()
  const themeElevation = useThemeElevation()

  return (
    <div
      css={[
        {
          alignItems: 'center',
          backgroundColor: getBackgroundColor(
            backgroundColorOverride,
            theme,
            themeElevation,
            isDisabled
          ),
          borderRadius: POG_SIZES[size] / 2,
          display: 'flex',
          height: POG_SIZES[size],
          justifyContent: 'center',
          width: POG_SIZES[size],
        },
        overrideRootStyles,
      ]}
    >
      {restProps.pictogram ? (
        <Pictogram
          color={getForegroundColor(
            isDisabled,
            theme.isDark,
            foregroundColorOverride
          )}
          type={restProps.pictogram}
        />
      ) : (
        <Type.Text
          color={getForegroundColor(
            isDisabled,
            theme.isDark,
            foregroundColorOverride,
            ExtendedPaletteColorName.Nova
          )}
          isBold
          size={size === 'standard' ? 'S' : 'L'}
        >
          {restProps.text}
        </Type.Text>
      )}
    </div>
  )
}

function getBackgroundColor(
  override: ColorToken | null | undefined,
  theme: ProductTheme,
  themeElevation: number,
  isDisabled: Props['isDisabled']
): string {
  // All disabled Pogs look disabled, even if an override is specified.
  if (isDisabled) {
    return theme.isDark ? CssVars.neutral.bg2 : CssVars.neutral.bg3
  }

  if (override) {
    return getColorValue(override, theme)
  }

  if (theme.isDark) {
    return CssVars.neutral.bg2
  }

  return getColorDefinition(ExtendedPaletteColorName.Jet, theme, themeElevation)
    .base
}

function getForegroundColor(
  isDisabled: Props['isDisabled'],
  themeIsDark: boolean,
  override: ColorToken | null | undefined,
  defaultColor?: ColorToken | null
): ColorToken | null | undefined {
  // All disabled Pogs look disabled, even if an override is specified.
  if (isDisabled) {
    return themeIsDark ? NeutralColorToken.Fg3 : ExtendedPaletteColorName.Nova
  }

  return override || defaultColor
}
